import { CalendarCheck, type LucideIcon } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { statusLabel } from '@/components/ui/primitives';
import { AnimatedNumber } from '@/features/dashboard/dashboard-ui';
import { BOARD_STATUSES, type StatusCounts, type StatusFilter } from './bed-status';
import { STATUS_BAR, STATUS_LOOK } from './bed-board-look';
import { OccupancyRing } from './bed-board-art';

function SummaryTile({
  label,
  value,
  sub,
  share,
  icon: Icon,
  tile,
  bar,
  ring,
  active,
  index,
  onClick,
}: {
  label: string;
  value: number;
  sub: string;
  share: number;
  icon: LucideIcon;
  tile: string;
  bar: string;
  ring: string;
  active: boolean;
  index: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={tr('ipd', 'show_only_these_beds')}
      style={{ animationDelay: `${index * 60}ms` }}
      className={cn(
        'group flex min-w-0 flex-col gap-2 rounded-xl border bg-background/60 p-3 text-start transition-[border-color,box-shadow,transform] focus-ring',
        'duration-500 fill-mode-backwards motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1',
        'hover:shadow-md motion-safe:hover:-translate-y-0.5',
        active && cn('ring-2 ring-offset-2 ring-offset-card', ring),
      )}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="min-w-0 text-xs font-medium leading-snug text-muted-foreground">{label}</span>
        <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset', tile)}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </span>
      <span className="text-2xl font-semibold leading-none">
        <AnimatedNumber value={value} />
      </span>
      <span className="flex flex-col gap-1.5">
        <span className="truncate text-[11px] text-muted-foreground">{sub}</span>
        <span className="h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
          <span
            className={cn('block h-full rounded-full transition-[width] duration-700 ease-out', bar)}
            style={{ width: `${Math.min(100, Math.max(0, share))}%` }}
          />
        </span>
      </span>
    </button>
  );
}

export function BedBoardSummary({
  counts,
  dischargesToday,
  dischargesTomorrow,
  statusFilter,
  onStatusFilter,
}: {
  counts: StatusCounts;
  dischargesToday: number;
  dischargesTomorrow: number;
  statusFilter: StatusFilter;
  onStatusFilter: (next: StatusFilter) => void;
}) {
  const shareOf = (value: number) => (counts.total ? Math.round((value / counts.total) * 100) : 0);
  const toggle = (next: StatusFilter) => onStatusFilter(statusFilter === next ? 'all' : next);

  return (
    <section className="mb-5 grid items-center gap-5 rounded-2xl border bg-card p-4 shadow-sm sm:p-5 lg:grid-cols-[auto_minmax(0,1fr)]">
      <div className="flex flex-col items-center gap-2">
        <OccupancyRing counts={counts} label={tr('ipd', 'occupancy')} />
        <p className="text-center text-xs text-muted-foreground">
          {tr('ipd', 'x_of_y_beds_occupied', { occupied: counts.occupied, total: counts.total })}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-6">
        {BOARD_STATUSES.map((status, index) => {
          const look = STATUS_LOOK[status];
          return (
            <SummaryTile
              key={status}
              label={statusLabel(status)}
              value={counts[status]}
              sub={tr('ipd', 'x_pct_of_beds', { value: shareOf(counts[status]) })}
              share={shareOf(counts[status])}
              icon={look.icon}
              tile={look.tile}
              bar={STATUS_BAR[status]}
              ring={look.ring}
              active={statusFilter === status}
              index={index}
              onClick={() => toggle(status)}
            />
          );
        })}
        <SummaryTile
          label={tr('ipd', 'expected_discharges_today')}
          value={dischargesToday}
          sub={tr('ipd', 'x_tomorrow', { count: dischargesTomorrow })}
          share={counts.occupied ? Math.round((dischargesToday / counts.occupied) * 100) : 0}
          icon={CalendarCheck}
          tile="bg-primary/10 text-primary ring-primary/20"
          bar="bg-primary"
          ring="ring-primary/60"
          active={statusFilter === 'discharge_today'}
          index={BOARD_STATUSES.length}
          onClick={() => toggle('discharge_today')}
        />
      </div>
    </section>
  );
}
